import { lazyScreen, RehearsalBar, ToastProvider, usePreloadWhenIdle } from '@croissants/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type JSX, type ReactNode, Suspense } from 'react';
import { Redirect, Route, Switch } from 'wouter';
import { Shell, SubNav } from './components/Shell';
import { LoadingPanel } from './components/states';
import { useT } from './i18n/index';
import { ApiProvider, config, SignInGate } from './lib/index';
import { DashboardScreen } from './screens/Dashboard';
import { DEBUG_LEGACY_SLUGS, DEBUG_SLUGS } from './screens/debug-slugs';
import { SETTINGS_SLUGS } from './screens/settings-slugs';

/**
 * Every screen but the landing one is its own chunk (docs/portal-latency-audit.html,
 * eighth pass: the entry chunk carried all of them, 664 KB before sign-in).
 *
 * The Admin dashboard stays in the entry because it is what `/` opens. The
 * rest are fetched in the background once the portal is idle - in rail order,
 * so the screens an operator reaches for first arrive first - which keeps the
 * promise this file used to keep by loading everything eagerly: an operator
 * navigating during an incident does not wait on a chunk, because by then it is
 * already in memory and `lazyScreen` renders it without a fallback. Only a cold
 * deep link shows the loading line, once, for as long as its one chunk takes.
 */
const OrdersScreen = lazyScreen(async () => (await import('./screens/Orders')).OrdersScreen);
const OrderDetailScreen = lazyScreen(
  async () => (await import('./screens/OrderDetail')).OrderDetailScreen,
);
const ActivityScreen = lazyScreen(async () => (await import('./screens/Activity')).ActivityScreen);
const ActiveBakeriesScreen = lazyScreen(
  async () => (await import('./screens/ActiveBakeries')).ActiveBakeriesScreen,
);
const BakeryHousesScreen = lazyScreen(
  async () => (await import('./screens/Bakeries')).BakeryHousesScreen,
);
const BakeryModeAHousesScreen = lazyScreen(
  async () => (await import('./screens/Bakeries')).BakeryModeAHousesScreen,
);
const BakeryModeAShopsScreen = lazyScreen(
  async () => (await import('./screens/Bakeries')).BakeryModeAShopsScreen,
);
const BakeryShopsScreen = lazyScreen(
  async () => (await import('./screens/Bakeries')).BakeryShopsScreen,
);
const BakeryHouseDetailScreen = lazyScreen(
  async () => (await import('./screens/BakeryDetail')).BakeryHouseDetailScreen,
);
const BakeryLocationDetailScreen = lazyScreen(
  async () => (await import('./screens/BakeryDetail')).BakeryLocationDetailScreen,
);
const CatalogueScreen = lazyScreen(
  async () => (await import('./screens/Catalogue')).CatalogueScreen,
);
const ProductDetailScreen = lazyScreen(
  async () => (await import('./screens/ProductDetail')).ProductDetailScreen,
);
const KindsScreen = lazyScreen(async () => (await import('./screens/Kinds')).KindsScreen);
const PropertiesScreen = lazyScreen(
  async () => (await import('./screens/Organizations')).PropertiesScreen,
);
const ClientDetailScreen = lazyScreen(
  async () => (await import('./screens/ClientDetail')).ClientDetailScreen,
);
const DevicesScreen = lazyScreen(async () => (await import('./screens/Devices')).DevicesScreen);
const DeviceDetailScreen = lazyScreen(
  async () => (await import('./screens/DeviceDetail')).DeviceDetailScreen,
);
const ReleasesScreen = lazyScreen(async () => (await import('./screens/Releases')).ReleasesScreen);
const FinanceOrdersScreen = lazyScreen(
  async () => (await import('./screens/FinanceOrders')).FinanceOrdersScreen,
);
const FinanceScreen = lazyScreen(async () => (await import('./screens/Finance')).FinanceScreen);
const PendingPayoutsScreen = lazyScreen(
  async () => (await import('./screens/PendingPayouts')).PendingPayoutsScreen,
);
const PayoutDetailScreen = lazyScreen(
  async () => (await import('./screens/PayoutDetail')).PayoutDetailScreen,
);
const MonthlyPayoutDetailScreen = lazyScreen(
  async () => (await import('./screens/MonthlyPayouts')).MonthlyPayoutDetailScreen,
);
const DeliveryStatementsScreen = lazyScreen(
  async () => (await import('./screens/DeliveryStatements')).DeliveryStatementsScreen,
);
const DeliveryStatementDetailScreen = lazyScreen(
  async () => (await import('./screens/DeliveryStatementDetail')).DeliveryStatementDetailScreen,
);
const DocumentsScreen = lazyScreen(
  async () => (await import('./screens/Documents')).DocumentsScreen,
);
const RatesScreen = lazyScreen(async () => (await import('./screens/Rates')).RatesScreen);
const FinancialParametersScreen = lazyScreen(
  async () => (await import('./screens/FinancialParameters')).FinancialParametersScreen,
);
const SettingsScreen = lazyScreen(async () => (await import('./screens/Settings')).SettingsScreen);
const QualityScreen = lazyScreen(async () => (await import('./screens/Quality')).QualityScreen);
const DebugScreen = lazyScreen(async () => (await import('./screens/Debug')).DebugScreen);
const PilotScreen = lazyScreen(async () => (await import('./screens/Pilot')).PilotScreen);
const SupportCasesScreen = lazyScreen(
  async () => (await import('./screens/Support')).SupportCasesScreen,
);
const SupportDecidedScreen = lazyScreen(
  async () => (await import('./screens/Support')).SupportDecidedScreen,
);
const SupportPaymentsScreen = lazyScreen(
  async () => (await import('./screens/SupportPayments')).SupportPaymentsScreen,
);

/**
 * The coverage map carries `mapbox-gl`, roughly four fifths of what this
 * portal would otherwise ship. It is the one chunk NOT fetched in the
 * background: a planning screen nobody opens at 06:20 does not get a
 * megabyte and a half of every operator's bandwidth on the off chance.
 */
const MapScreen = lazyScreen(async () => (await import('./screens/Map')).MapScreen);

/** Rail order: what an operator opens first is fetched first. */
const PRELOADED_SCREENS = [
  OrdersScreen,
  OrderDetailScreen,
  ActivityScreen,
  ActiveBakeriesScreen,
  BakeryHousesScreen,
  BakeryHouseDetailScreen,
  CatalogueScreen,
  ProductDetailScreen,
  KindsScreen,
  PropertiesScreen,
  ClientDetailScreen,
  DevicesScreen,
  DeviceDetailScreen,
  ReleasesScreen,
  SupportCasesScreen,
  SupportDecidedScreen,
  SupportPaymentsScreen,
  FinanceOrdersScreen,
  FinanceScreen,
  PendingPayoutsScreen,
  PayoutDetailScreen,
  MonthlyPayoutDetailScreen,
  DeliveryStatementsScreen,
  DeliveryStatementDetailScreen,
  DocumentsScreen,
  RatesScreen,
  FinancialParametersScreen,
  SettingsScreen,
  QualityScreen,
  DebugScreen,
  PilotScreen,
] as const;

/**
 * internal. - the operations admin dashboard. Its own identity application with MFA
 * enforced and no user pool shared with any other portal (tech-stack section 7).
 *
 * Routing is client-side and the worker serves the SPA fallback, so every path
 * below is reachable by deep link - an operator pasting an order URL into a
 * message during an incident is the normal case, not the edge one.
 */
export function App(): JSX.Element {
  const t = useT();
  // Started before the sign-in gate settles: the chunks are static files, and
  // the time Google and Clerk take is time they can arrive in.
  usePreloadWhenIdle(PRELOADED_SCREENS);
  return (
    <SignInGate>
      <ApiProvider>
        {/* The confirmation a write earns - "Sent to marie@..." - which must
            not become a banner the operator dismisses before carrying on. Above
            the Shell so any screen under it can raise one (packages/ui toast). */}
        <ToastProvider closeLabel={t('action.close')} regionLabel={t('toast.region')}>
          <Rehearsal />
          <Shell>
            {/* Inside the Shell, so the rail stays while a cold deep link's
                chunk arrives. */}
            <Suspense fallback={<LoadingPanel />}>
              <Switch>
                <Route path="/" component={DashboardScreen} />
                {/* The order record's tabs (founder, 2026-10-19); the bare path is
                the summary, so every link already printed still opens it. */}
                <Route path="/orders/:orderId/financials">
                  {(params) => (
                    <OrderDetailScreen orderId={params.orderId ?? ''} section="financials" />
                  )}
                </Route>
                <Route path="/orders/:orderId/support">
                  {(params) => (
                    <OrderDetailScreen orderId={params.orderId ?? ''} section="support" />
                  )}
                </Route>
                <Route path="/orders/:orderId/activity">
                  {(params) => (
                    <OrderDetailScreen orderId={params.orderId ?? ''} section="activity" />
                  )}
                </Route>
                <Route path="/orders/:orderId/invoices">
                  {(params) => (
                    <OrderDetailScreen orderId={params.orderId ?? ''} section="invoices" />
                  )}
                </Route>
                <Route path="/orders/:orderId">
                  {(params) => <OrderDetailScreen orderId={params.orderId ?? ''} />}
                </Route>
                <Route path="/activity" component={ActivityScreen} />
                {/* Where Alerts used to live. Same reason every other moved
                screen kept its old path: an operator's bookmark, and every link
                pasted into an incident thread before the feed existed, lands on
                what it used to show - the alerts, filtered to alerts - rather
                than on Not found (OPS-027). */}
                <Route path="/alerts">
                  <Redirect to="/activity?kinds=ALERT" replace />
                </Route>
                <Route path="/orders" component={OrdersScreen} />
                {/* Health status, Dead letter, Kill switch, Audit log and
                Scenario runs, behind one sidebar destination. Still one route
                each, so a deep link into any of them keeps working.

                Scenario runs is the one section whose rows open a page of
                their own, so it takes a second segment: `/debug/scenario-runs/
                <run id>`. Declared BEFORE `/debug/:section`, which would
                otherwise not match it at all, and it goes through the same
                `DebugScreen` so the section's strip stays above the record. */}
                <Route path="/debug/:section/:record">
                  {(params) => (
                    <DebugScreen section={params.section ?? ''} record={params.record ?? ''} />
                  )}
                </Route>
                <Route path="/debug/:section">
                  {(params) => <DebugScreen section={params.section ?? ''} />}
                </Route>
                <Route path="/debug">
                  <Redirect to={`/debug/${DEBUG_SLUGS[0] ?? ''}`} replace />
                </Route>
                {/* Where the FOUR original sections used to live, and only those
                (`DEBUG_LEGACY_SLUGS`, which says why it is not every slug).
                Same reason the Organizations split kept its old path: an
                operator's bookmark, and every link pasted into an incident
                thread before the grouping, still lands on the screen rather
                than on Not found. */}
                {DEBUG_LEGACY_SLUGS.map((slug) => (
                  <Route key={slug} path={`/${slug}`}>
                    <Redirect to={`/debug/${slug}`} replace />
                  </Route>
                ))}
                {/* One bakery record, opened by clicking its name on any of the
                three tabs. No sub-navigation: the strip names the three lists,
                and this is one of the things they list. */}
                <Route path="/organizations/bakeries/shops/:locationId">
                  {(params) => <BakeryLocationDetailScreen locationId={params.locationId ?? ''} />}
                </Route>
                <Route path="/organizations/bakeries/houses/:organizationId/payouts">
                  {(params) => (
                    <BakeryHouseDetailScreen
                      organizationId={params.organizationId ?? ''}
                      section="payouts"
                    />
                  )}
                </Route>
                <Route path="/organizations/bakeries/houses/:organizationId">
                  {(params) => (
                    <BakeryHouseDetailScreen organizationId={params.organizationId ?? ''} />
                  )}
                </Route>
                {/* Every active shop of both modes as it stands today, with its
                lanes and its devices (OPS-001, OPS-035). The section's first
                tab; it moved here from under the Admin dashboard's board. */}
                <Route path="/organizations/bakeries/active">
                  <BakeriesSection>
                    <ActiveBakeriesScreen />
                  </BakeriesSection>
                </Route>
                {/* The four lists, mode in the path. Each mode has BOTH kinds -
                the houses that sell into that pool, and the counters those
                houses run - so the segment names the mode and the leaf names
                the kind, and neither has to be inferred from the other. */}
                <Route path="/organizations/bakeries/mode-a/houses">
                  <BakeriesSection>
                    <BakeryModeAHousesScreen />
                  </BakeriesSection>
                </Route>
                <Route path="/organizations/bakeries/mode-a/shops">
                  <BakeriesSection>
                    <BakeryModeAShopsScreen />
                  </BakeriesSection>
                </Route>
                <Route path="/organizations/bakeries/mode-b/houses">
                  <BakeriesSection>
                    <BakeryHousesScreen />
                  </BakeriesSection>
                </Route>
                <Route path="/organizations/bakeries/mode-b/shops">
                  <BakeriesSection>
                    <BakeryShopsScreen />
                  </BakeriesSection>
                </Route>
                {/* The two paths the mode B lists used to own, before the mode
                moved into the path. A bookmark, and any link pasted into an
                incident thread, still lands on the list it named rather than on
                Not found - the same rule the Organizations split followed. Both
                sit AFTER the `:id` detail routes above, which they would
                otherwise shadow. */}
                <Route path="/organizations/bakeries/shops">
                  <Redirect to="/organizations/bakeries/mode-b/shops" replace />
                </Route>
                <Route path="/organizations/bakeries/houses">
                  <Redirect to="/organizations/bakeries/mode-b/houses" replace />
                </Route>
                {/* The curated platform range IS a mode A fact - it is what a mode A
                shop bakes and the only storefront it feeds - so it is a tab of
                the Bakeries section rather than a sidebar destination of its
                own, which asked an operator to hold "Menu" and "Mode A" apart
                as two unrelated ideas. */}
                {/* ONE product, on its own page, with its four tabs as four
                routes - so a card can be linked to directly. Both the tabbed
                path and the bare one match ONE `<Route>` element, which is what
                keeps the form mounted while a curator moves between tabs: two
                routes would remount it and lose half an edit. */}
                <Route path="/organizations/bakeries/menu/:itemId/:tab">
                  {(params) => (
                    <BakeriesSection>
                      <ProductDetailScreen itemId={params.itemId} tab={params.tab} />
                    </BakeriesSection>
                  )}
                </Route>
                <Route path="/organizations/bakeries/menu/:itemId">
                  {(params) => (
                    <BakeriesSection>
                      <ProductDetailScreen itemId={params.itemId} tab="description" />
                    </BakeriesSection>
                  )}
                </Route>
                <Route path="/organizations/bakeries/menu">
                  <BakeriesSection>
                    <CatalogueScreen />
                  </BakeriesSection>
                </Route>
                {/* The sections that menu is divided into, and the words over
                each of them in both languages (CAT-016). Its own tab beside the
                menu rather than a control inside the product form: a kind is a
                record two languages have to agree about, and the form could
                only ever collect the half whoever was typing was thinking in. */}
                <Route path="/organizations/bakeries/kinds">
                  <BakeriesSection>
                    <KindsScreen />
                  </BakeriesSection>
                </Route>
                {/* The destination's own path lands on its first tab, so every
                bookmark still lands somewhere real - the same rule the
                Organizations split followed below. */}
                <Route path="/organizations/bakeries">
                  <Redirect to="/organizations/bakeries/active" replace />
                </Route>
                {/* One client record, opened by clicking its name on the list. The
                segment is the organization's own REFERENCE rather than its wire
                id - `OrganizationRef` accepts both and the reference is what an
                operator can read off an audit row and paste here. */}
                <Route path="/organizations/properties/:organizationId/payouts">
                  {(params) => (
                    <ClientDetailScreen
                      organizationId={params.organizationId ?? ''}
                      section="payouts"
                    />
                  )}
                </Route>
                <Route path="/organizations/properties/:organizationId">
                  {(params) => <ClientDetailScreen organizationId={params.organizationId ?? ''} />}
                </Route>
                <Route path="/organizations/properties" component={PropertiesScreen} />
                {/* The single Organizations screen these two replaced. An operator's
                bookmark, and any link pasted into an incident thread before the
                split, still lands somewhere real rather than on Not found. */}
                <Route path="/organizations">
                  <Redirect to="/organizations/properties" replace />
                </Route>
                {/* Where the range used to have its own sidebar entry. Same reason
                the Organizations split kept its old path: a bookmark, or a link
                pasted into an incident thread, lands on the screen that answers
                the question rather than on Not found. */}
                <Route path="/catalogue">
                  <Redirect to="/organizations/bakeries/menu" replace />
                </Route>
                {/* The counter fleet (OPS-029): the devices, and the builds
                they are offered. `/devices/releases` is declared FIRST so it is
                not shadowed by the index route below it. */}
                <Route path="/devices/releases">
                  <DevicesSection>
                    <ReleasesScreen />
                  </DevicesSection>
                </Route>
                <Route path="/devices">
                  <DevicesSection>
                    <DevicesScreen />
                  </DevicesSection>
                </Route>
                {/* One counter's own page, opened by clicking its label on the
                fleet list. No sub-navigation, for the reason the bakery record
                has none: the strip names the lists, and this is one of the
                things they list. Declared after both, so neither is shadowed. */}
                <Route path="/devices/:deviceId">
                  {(params) => <DeviceDetailScreen deviceId={params.deviceId ?? ''} />}
                </Route>
                {/* Support (issue #2272): Cases, with the case beside the
                list and deep-linkable; Decided, the table; and Payments. A
                case is opened from its order's page, never from here. */}
                <Route path="/support/payments" component={SupportPaymentsScreen} />
                <Route path="/support/decided" component={SupportDecidedScreen} />
                <Route path="/support/cases/:caseId">
                  {(params) => <SupportCasesScreen caseId={params.caseId ?? ''} />}
                </Route>
                <Route path="/support">
                  <SupportCasesScreen />
                </Route>
                {/* The money waiting on an Internal decision, which is the one
                thing on this section somebody DOES rather than reads - so it
                is a tab of its own, and a table screen like Orders. It was a
                card stacked on the ledger tab until 5 September 2026; see
                `screens/PendingPayouts.tsx`. */}
                <Route path="/finance/payouts">
                  <FinanceSection>
                    <PendingPayoutsScreen />
                  </FinanceSection>
                </Route>
                {/* One organization's payout preview, opened from its row: the
                frozen amount, the movements it pays and the approval. No
                sub-navigation, for the reason the counter record has none: the
                strip names the lists, and this is one of the things they list.
                Declared AFTER the list so neither is shadowed. */}
                {/* Croissant's own cycle: its monthly statement and payout to Qonto.
                Declared BEFORE the organization's page, which would read
                `croissant` as an organization id. */}
                <Route path="/finance/payouts/croissant/:cycle">
                  {(params) => <MonthlyPayoutDetailScreen cycle={params.cycle ?? ''} />}
                </Route>
                <Route path="/finance/payouts/:organizationId">
                  {(params) => <PayoutDetailScreen organizationId={params.organizationId ?? ''} />}
                </Route>
                {/* One statement per delivery provider cycle, matched against the
                provider's invoice (docs/finance/reconciliation.html#delivery-statement).
                The statement itself has no sub-navigation, like a payout's. */}
                <Route path="/finance/delivery-statements">
                  <FinanceSection>
                    <DeliveryStatementsScreen />
                  </FinanceSection>
                </Route>
                <Route path="/finance/delivery-statements/:cycle">
                  {(params) => <DeliveryStatementDetailScreen cycle={params.cycle ?? ''} />}
                </Route>
                {/* One screen again since 17 September 2026: every figure is a
                column of a client's own card, so the second tab that held three
                of them has nothing left to show. Its path stays, for a bookmark. */}
                <Route path="/finance/rates/thresholds">
                  <Redirect to="/finance/rates" replace />
                </Route>
                {/* Every issued invoice, credit note and statement, and where each
                stands with Pennylane (docs/finance/invoicing.html#delivery). */}
                <Route path="/finance/documents">
                  <Redirect to="/finance/customer-invoices" replace />
                </Route>
                <Route path="/finance/customer-invoices">
                  <FinanceSection>
                    <DocumentsScreen key="CUSTOMER" category="CUSTOMER" />
                  </FinanceSection>
                </Route>
                <Route path="/finance/supplier-invoices">
                  <FinanceSection>
                    <DocumentsScreen key="SUPPLIER" category="SUPPLIER" />
                  </FinanceSection>
                </Route>
                {/* Per delivered order: its money, its net margin and the payout
                each share left in (docs/finance/reconciliation.html#finance-screen). */}
                <Route path="/finance/orders">
                  <FinanceSection>
                    <FinanceOrdersScreen />
                  </FinanceSection>
                </Route>
                <Route path="/finance/parameters">
                  <FinanceSection>
                    <FinancialParametersScreen />
                  </FinanceSection>
                </Route>
                <Route path="/finance/rates">
                  <FinanceSection>
                    <RatesScreen />
                  </FinanceSection>
                </Route>
                <Route path="/finance">
                  <FinanceSection>
                    <FinanceScreen />
                  </FinanceSection>
                </Route>
                {/* The top-level Commercial rates entry that became a Finance
                sub-tab. Same reason the Organizations split kept its old path:
                a bookmark, or a link pasted into an incident thread, lands on
                the screen that answers the question rather than on Not found. */}
                <Route path="/rates">
                  <Redirect to="/finance/rates" replace />
                </Route>
                {/* The administered platform settings (OPS-025): five tabs, one
                route each, in order of importance. A bare `/settings` - the
                rail entry and every link to it before the split - opens the
                first. */}
                <Route path="/settings/:section">
                  {(params) => <SettingsScreen section={params.section ?? ''} />}
                </Route>
                <Route path="/settings">
                  <Redirect to={`/settings/${SETTINGS_SLUGS[0]}`} replace />
                </Route>
                <Route path="/quality" component={QualityScreen} />
                {/* The pilot launcher, which used to be a static page on `www.`.
                Reachable typed cold: signed out this renders the portal's own
                sign-in and returns here afterwards (`lib/return-to.ts`), which
                matters because it is now the only page that says how to open the
                other three portals.

                DEMO INSTANCE ONLY, and the route goes rather than the screen
                refusing: on a production build there is no `/pilot` at all, so a
                bookmark falls through to Not found like any other path this
                portal does not serve. A screen that rendered and explained
                itself would still be a screen offering a cookie the API refuses
                (docs/auth-and-access.html section 4). `Shell.tsx` drops the rail
                entry on the same build. */}
                {config.isProductionInstance ? null : (
                  <Route path="/pilot" component={PilotScreen} />
                )}
                <Route path="/map">
                  <MapScreen />
                </Route>
                {/* The sector form this replaced. Same reason the Organizations
                split kept its old path: a bookmark, or a link pasted into an
                incident thread, lands on the screen that answers the question
                rather than on Not found. */}
                <Route path="/geography">
                  <Redirect to="/map" replace />
                </Route>
                <Route component={NotFound} />
              </Switch>
            </Suspense>
          </Shell>
        </ToastProvider>
      </ApiProvider>
    </SignInGate>
  );
}

/**
 * The rehearsal strip (TST-003). Dev builds only, and it renders nothing at all
 * unless this API serves `/dev/rehearsal`.
 *
 * Its own component because it needs the query client, and `App` is above the
 * hook that provides one. The refetch is filtered to the READS - every GET this
 * portal has made - because a platform clock that jumped to tomorrow morning
 * can change the answer to any of them, and to none of the writes. A blanket
 * `invalidateQueries()` is refused by `scripts/check-write-refetch.mjs`.
 */
function Rehearsal(): JSX.Element | null {
  const queryClient = useQueryClient();
  if (!config.isDevBuild) return null;
  return (
    <RehearsalBar
      apiBaseUrl={config.apiBaseUrl}
      onPlatformTimeChanged={() =>
        void queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === 'GET',
        })
      }
    />
  );
}

/**
 * Finance is one sidebar entry owning the ledger and reconciliation state and
 * the rate book those figures are computed from. They are the same job - the
 * rate book is what the ledger's splits mean - and Commercial rates was a
 * sidebar entry nobody visited except from Finance.

 *
 * The tab labels ARE the screens' own titles rather than copy of their own, so
 * the strip and the heading below it cannot drift apart.
 */
/**
 * Bakeries is one sidebar entry owning the active bakeries table, the four
 * lists (plan 3.1) and the menus they bake. A bakery's storefront mode is
 * immutable (OPS-013), so the mode is what the strip states for the four lists
 * and those tables carry no column for it.
 *
 * The tab labels ARE the screens' own titles, like Finance's and Debug's, so the
 * strip and the heading below it cannot drift apart.
 */
function BakeriesSection({ children }: { children: ReactNode }): JSX.Element {
  const t = useT();
  return (
    <>
      <SubNav
        label={t('nav.bakeries')}
        /* Active bakeries FIRST: today's state of every trading shop, both
           modes in one table. Then the lists grouped by mode, mode A first, and
           the house before its shops within each - the house is the tenant and
           a shop is one of its addresses. The menus come LAST because they are
           not a list of organizations at all: they are what all of those
           organizations sell, and one catalogue covers both modes (CAT-013). */
        items={[
          { href: '/organizations/bakeries/active', label: t('health.bakeries.title') },
          { href: '/organizations/bakeries/mode-a/houses', label: t('bakeries.modeAHouses.title') },
          { href: '/organizations/bakeries/mode-a/shops', label: t('bakeries.modeAShops.title') },
          { href: '/organizations/bakeries/mode-b/houses', label: t('bakeries.houses.title') },
          { href: '/organizations/bakeries/mode-b/shops', label: t('bakeries.shops.title') },
          { href: '/organizations/bakeries/menu', label: t('catalogue.title') },
          { href: '/organizations/bakeries/kinds', label: t('kinds.title') },
        ]}
      />
      {children}
    </>
  );
}

/**
 * Devices is one sidebar entry owning two screens: the fleet itself, and the
 * builds it is offered. They are one job - a rollout is read on the list and
 * driven from the releases panel - and neither is worth a rail entry alone.
 *
 * The tab labels ARE the screens' own titles, like Finance's, Debug's and the
 * Bakeries strip's, so the strip and the heading below it cannot drift apart.
 */
function DevicesSection({ children }: { children: ReactNode }): JSX.Element {
  const t = useT();
  return (
    <>
      <SubNav
        label={t('nav.devices')}
        items={[
          { href: '/devices', label: t('devices.title') },
          { href: '/devices/releases', label: t('releases.title') },
        ]}
      />
      {children}
    </>
  );
}

function FinanceSection({ children }: { children: ReactNode }): JSX.Element {
  const t = useT();
  return (
    <>
      <SubNav
        label={t('nav.finance')}
        items={[
          { href: '/finance/orders', label: t('finance.orders.title') },
          { href: '/finance', label: t('finance.title') },
          { href: '/finance/payouts', label: t('finance.pending.title') },
          { href: '/finance/delivery-statements', label: t('finance.delivery.title') },
          { href: '/finance/customer-invoices', label: t('documents.customer.title') },
          { href: '/finance/supplier-invoices', label: t('documents.supplier.title') },
          { href: '/finance/rates', label: t('rates.title') },
          { href: '/finance/parameters', label: t('parameters.title') },
        ]}
      />
      {children}
    </>
  );
}

function NotFound(): JSX.Element {
  const t = useT();
  return <p className="internal-muted">{t('error.notFound')}</p>;
}
